import { applyLimits, computeLocaleSeps, sanitize, splitDecimal } from '@shared/helpers/numeric-input.helper';
import * as _ from 'lodash';
import { MyVerbale } from '../../features/revisione53/core/models/interfaces/revisione.interface';
import { toJsDateMsOrNull, toYearRangeOrNull } from '@shared/helpers/date.helper';
import { isIntegerString, toNum } from '@shared/helpers/math.helper';
import { FormGroup } from '@angular/forms';
import { asyncScheduler, combineLatest, debounceTime, delay, distinctUntilChanged, merge, Observable, observeOn, of, shareReplay, skipUntil, startWith, timer } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { getControl } from '@shared/helpers/form.helper';
import { Utilities } from '@core/helpers/utilities';

export const isNilOrEmpty = (v: unknown): boolean => _.isNil(v) || (typeof v === 'string' && removeAllWhiteSpace(v) === '') || (Array.isArray(v) && v?.length === 0);

export const isNilOrEmptyOrZero = (v: unknown): boolean => isNilOrEmpty(v) || v === 0;

export const toBoolean = (value: unknown): boolean => {
    if (isNilOrEmpty(value) || value === false || value === 0 || value === '0' || value === 'false') {
        return false;
    }
    return value === true || value === 1 || value === '1' || value === 'true';
};

export const sortByFieldAlphabetically = <T>(list: T[], field: keyof T, direction: 'asc' | 'desc' = 'asc'): T[] => {
    return [...list].sort((a, b) => {
        const valueA = String(a[field] ?? '').toLowerCase();
        const valueB = String(b[field] ?? '').toLowerCase();

        const result = valueA.localeCompare(valueB, 'it', {
            sensitivity: 'base',
        });

        return direction === 'asc' ? result : -result;
    });
};

export const areAllFieldsNilOrEmpty = <T extends object>(obj: T, excludedKeys: string[] = []): boolean => {
    const excludedKeysSet = new Set(excludedKeys);

    return Object.entries(obj).every(([key, v]) => {
        if (excludedKeysSet.has(key)) {
            return true;
        }

        if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
            return areAllFieldsNilOrEmpty(v, excludedKeys);
        }

        if (isNilOrEmpty(v)) {
            return true;
        }

        if (typeof v === 'number') {
            return v === 0;
        }

        if (typeof v === 'string') {
            const normalized = removeAllWhiteSpace(v);
            const n = toNum(normalized);

            if (Number.isFinite(n)) {
                return n === 0;
            }

            return false;
        }

        return false;
    });
};

export const removeAllWhiteSpace = (value: string): string => {
    return value.replace(/\s+/g, '');
};

export const updateDependentControl = (form: FormGroup, primaryPath: string, dependentPath: string): void => {
    const primaryControl = getControl(form, primaryPath);
    const dependentControl = getControl(form, dependentPath);

    primaryControl.valueChanges.pipe(startWith(primaryControl.value), distinctUntilChanged()).subscribe((value) => {
        dependentControl.patchValue(value, { emitEvent: false });
    });
};

export const controlFormStatus = (form: FormGroup, externalSettled$?: Observable<boolean>): Observable<boolean> => {
    const settled$ = timer(Utilities.FORM_STATUS_SETTLE_MS).pipe(shareReplay(1));

    const status$ = merge(
        of(form.valid),
        settled$.pipe(
            delay(Utilities.LISTENERS_DEBOUNCE_TIME),
            map(() => form.valid)
        ),
        merge(form.valueChanges, form.statusChanges).pipe(
            skipUntil(settled$),
            debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME),
            filter(() => !form.pending),
            map(() => form.valid)
        )
    );

    const gated$ = externalSettled$
        ? combineLatest([status$, externalSettled$]).pipe(
              filter(([, settled]) => settled && !form.pending),
              map(() => form.valid)
          )
        : status$;

    return gated$.pipe(distinctUntilChanged(), observeOn(asyncScheduler), shareReplay({ bufferSize: 1, refCount: true }));
};

export const trimLeftCharKeepOne = (input: string, ch: string): string => {
    if (!input || !ch || ch.length !== 1) {
        return input;
    }

    let i = 0;
    while (i < input.length - 1 && input[i] === ch) {
        i++;
    }
    return input.slice(i);
};

export const replaceAll = (text: string, targetValue: string, newValue: string): string => {
    return text.split(targetValue).join(newValue);
};

export const formatNumericString = (
    input: string | number | null | undefined,
    locale: string = 'it-IT',
    allowNegative: boolean = true,
    maxIntegerDigits: number = 0,
    maxFractionDigits: number = 2
): string => {
    const { groupSep, decimalSep } = computeLocaleSeps(locale);
    const s = sanitize(String(input ?? ''), allowNegative, groupSep, decimalSep, maxFractionDigits);
    const body0 = s.body ?? '';
    if (!body0) {
        return s.negative ? '-' : '';
    }

    const body = applyLimits(body0, maxIntegerDigits, maxFractionDigits, decimalSep);

    const { intPart: rawInt, fracPart: rawFrac, separatorChar } = splitDecimal(body, decimalSep);
    let intPart = trimLeftCharKeepOne(rawInt ?? '', '0');
    if (!intPart) {
        intPart = '0';
    }

    const intFormatted = new Intl.NumberFormat(locale, {
        useGrouping: true,
        maximumFractionDigits: 0,
    }).format(Number(intPart));

    const hasSep = !!separatorChar;
    const frac = rawFrac ?? '';

    const formattedBody = hasSep ? `${intFormatted}${decimalSep}${frac}` : intFormatted;
    return s.negative ? `-${formattedBody}` : formattedBody;
};

export const customToArray = <T>(v: unknown): T[] => {
    return Array.isArray(v) ? v : [];
};

export const checkMatricola = (myVerbale: MyVerbale) => {
    const matricolaCached = sessionStorage.getItem('matricola');
    const matricolaVerbale = myVerbale?.my_SezioneI__Rilevazione?.my_DatiIdentificativi?.my_Matricola;
    return !!myVerbale && matricolaVerbale === matricolaCached;
};

export const checkIdAssegnazione = (myVerbale: MyVerbale) => {
    const idAssegnazioneCached = sessionStorage.getItem('verbale_idAssegnazione');
    const idAssegionazioneVerbale = myVerbale?.my_IdAssegnazione;
    return !!myVerbale && idAssegionazioneVerbale === idAssegnazioneCached;
};

export const compareStringsSmart = (a: string, b: string): number => {
    const stringCollator = new Intl.Collator('it', { sensitivity: 'base', numeric: true });

    const leftText = (a ?? '').trim();
    const rightText = (b ?? '').trim();

    const leftIsEmpty = isNilOrEmpty(leftText);
    const rightIsEmpty = isNilOrEmpty(rightText);

    if (leftIsEmpty && rightIsEmpty) {
        return 0;
    }

    if (leftIsEmpty) {
        return 1;
    }

    if (rightIsEmpty) {
        return -1;
    }

    const leftDateMs = toJsDateMsOrNull(leftText);
    const rightDateMs = toJsDateMsOrNull(rightText);

    const leftIsDate = leftDateMs !== null;
    const rightIsDate = rightDateMs !== null;

    if (leftIsDate && !rightIsDate) {
        return -1;
    }

    if (!leftIsDate && rightIsDate) {
        return 1;
    }

    if (leftIsDate && rightIsDate) {
        if (leftDateMs === rightDateMs) {
            return 0;
        }

        return leftDateMs < rightDateMs ? -1 : 1;
    }

    const leftYearRange = toYearRangeOrNull(leftText);
    const rightYearRange = toYearRangeOrNull(rightText);

    if (leftYearRange !== null && rightYearRange !== null) {
        if (leftYearRange.start !== rightYearRange.start) {
            return leftYearRange.start < rightYearRange.start ? -1 : 1;
        }

        if (leftYearRange.end !== rightYearRange.end) {
            return leftYearRange.end < rightYearRange.end ? -1 : 1;
        }

        return 0;
    }

    const leftIsInteger = isIntegerString(leftText);
    const rightIsInteger = isIntegerString(rightText);

    if (leftIsInteger && rightIsInteger) {
        const leftNumber = Number(leftText);
        const rightNumber = Number(rightText);

        if (leftNumber === rightNumber) {
            return 0;
        }

        return leftNumber < rightNumber ? -1 : 1;
    }

    return stringCollator.compare(leftText, rightText);
};

export const stringLengthInfoPathLike = (value: unknown): number => {
    if (value === null || value === undefined) {
        return 0;
    }

    if (Array.isArray(value)) {
        return value.length === 0 ? 0 : stringLengthInfoPathLike(value[0]);
    }

    if (value instanceof Date) {
        return value.toISOString().length;
    }

    return String(value).length;
};

export const nz = (value: unknown): number => {
    if (value === null || value === undefined || value === '') {
        return 0;
    }

    const normalizedValue = String(value).replace(',', '.');
    const numericValue = Number(normalizedValue);

    return Number.isNaN(numericValue) ? 0 : numericValue;
};
